// LandingRegistrationVazlatScreen — a Landing Varázsló saját címvázlat
// képernyője. Vizuálisan és szerkezetileg a `MagnetPostFacebook.js`
// vázlatképernyőjének fizikailag különálló, pixelpontos másolata:
// felső sáv (Beállítás / Webinár kapcsolóval), 440px bal panel finomító
// chat mezővel, jobb oldali krémszínű vászon a 3 cím-alcím opcióval.
//
// TELJESEN különálló a `WebinarVazlatScreen`-től és a Facebook posztos
// vázlattól — nem importálunk semmit belőlük, csak identikus vizuál.
// Így a Landing Varázsló későbbi átalakítása NEM érinti sem a Webinárt,
// sem a Facebook / Carousel poszt-vázlatot.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle,
  CircleNotch,
  ArrowLeft,
  Paperclip,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PURPLE_ACCENT = "#7c6cf2";

export default function LandingRegistrationVazlatScreen({
  inputs,
  titleOptions,
  onOptionsRefreshed,     // (newOptions) => void — a szülő state frissítése
  onOptionChosen,         // (option) => void  — a szülő indítja a section flow-t
  onBack,                 // () => void — a Beállítás fülre / vissza gombra
}) {
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);
  const [expandingId, setExpandingId] = useState(null);
  const options = Array.isArray(titleOptions) ? titleOptions : [];
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  const requestRefine = async (instructionOverride) => {
    const instruction = (instructionOverride || refineText).trim();
    if (!instruction || refining) return;
    setRefining(true);
    setRefineText("");
    try {
      const startRes = await api.post(
        "/conversion/landing-registration/vazlat/refine",
        {
          webinar_cim: inputs?.webinar_cim || "",
          mit_tanuljon: inputs?.mit_tanuljon || "",
          miert_te: inputs?.miert_te || "",
          hova_hivjuk: inputs?.hova_hivjuk || "",
          refine: instruction,
        },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a finomítást.");
      for (let attempt = 0; attempt < 30; attempt++) {
        await new Promise((r) => setTimeout(r, 3500));
        const poll = await api.get(
          `/conversion/landing-registration/vazlat/job/${jobId}`,
        );
        const status = poll.data?.status;
        if (status === "done") {
          const opts = poll.data?.result?.title_options || [];
          if (opts.length >= 3) {
            onOptionsRefreshed(opts);
            toast.success("Új címek elkészültek.");
          } else {
            toast.error("A finomítás nem adott 3 opciót — próbáld újra.");
          }
          setRefining(false);
          return;
        }
        if (status === "error") {
          throw new Error(poll.data?.error || "Finomítás sikertelen.");
        }
      }
      throw new Error("Időtúllépés — a finomítás túl sokáig tart.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Finomítás sikertelen.";
      toast.error(msg);
      setRefining(false);
    }
  };

  const handleOptionExpand = async (opt) => {
    if (expandingId) return;
    setExpandingId(opt.id);
    try {
      await api.post("/conversion/landing-registration/vazlat/apply", {
        option_id: opt.id,
        direction: opt.direction || "",
        webinar_name: opt.webinar_name || null,
        title: opt.title || "",
        subtitle: opt.subtitle || "",
      });
    } catch { /* apply szerver-oldalon csak validál — hibát nem blokkolunk */ }
    onOptionChosen(opt);
  };

  const inputDisabled = refining;

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", overflow: "hidden" }}
      data-testid="landing-titles-shell"
    >
      <LandingTitlesHeader onBackToOpening={onBack} />

      <div
        className="relative flex-1 min-h-0"
        style={{ overflow: "hidden" }}
        data-testid="landing-titles-body"
      >
        <motion.div
          key="output"
          initial={{ x: 30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.28, ease: [0.22, 0.9, 0.32, 1] }}
          className="absolute inset-0 flex flex-col"
        >
          <LandingTitlesOutputView
            options={options}
            refineText={refineText}
            setRefineText={setRefineText}
            refining={refining}
            expandingId={expandingId}
            inputDisabled={inputDisabled}
            onSubmitChat={() => requestRefine()}
            onOptionExpand={handleOptionExpand}
            isMac={isMac}
          />
        </motion.div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Header (Beállítás / Webinár tabok) ─────────────────────────── */

function LandingTitlesHeader({ onBackToOpening }) {
  const tab1Ref = useRef(null);
  const tab2Ref = useRef(null);
  const [dims, setDims] = useState({ left1: 0, w1: 0, left2: 0, w2: 0 });
  const recompute = () => {
    const t1 = tab1Ref.current, t2 = tab2Ref.current;
    if (!t1 || !t2) return;
    setDims({ left1: t1.offsetLeft, w1: t1.offsetWidth, left2: t2.offsetLeft, w2: t2.offsetWidth });
  };
  useLayoutEffect(() => { recompute(); }, []);
  useEffect(() => {
    const r = () => recompute();
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);
  // Az aktív fül mindig a "Webinár" — a "Beállítás" fülre kattintás a
  // szülőben visszavisz a setup fázisba (onBackToOpening).
  const sliderLeft = dims.left2;
  const sliderWidth = dims.w2;

  return (
    <header
      className="sticky top-0 z-40 flex items-center justify-between gap-4"
      style={{ padding: "14px 22px", background: "#0d0d16", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      data-testid="landing-titles-header"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBackToOpening}
          className="inline-flex items-center gap-1.5 transition hover:text-white"
          style={{ fontSize: 12, color: "#7a7a95", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}
          data-testid="landing-titles-back-to-opening"
        >
          <ArrowLeft size={13} weight="bold" /> Vissza
        </button>
        <span style={{ width: 1, height: 14, background: "rgba(255,255,255,0.10)" }} />
        <span
          aria-hidden="true"
          style={{
            width: 24, height: 24, borderRadius: 6,
            background: `linear-gradient(135deg, ${PURPLE_ACCENT}, #4e3fd8)`,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 14px -6px ${PURPLE_ACCENT}aa`,
          }}
        >
          <Sparkle size={13} weight="fill" color="white" />
        </span>
        <span className="truncate" style={{ fontSize: 14, color: "white", fontWeight: 500, letterSpacing: "-0.005em" }}>
          Landing Varázsló
        </span>
      </div>

      <div
        style={{
          height: 38, background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 8, padding: 3,
          display: "flex", alignItems: "center", position: "relative",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute", top: 3, bottom: 3,
            left: sliderLeft, width: sliderWidth,
            background: "rgba(124,108,242,0.18)",
            border: "1px solid rgba(124,108,242,0.28)",
            borderRadius: 6,
            transition: "left 0.22s ease-out, width 0.22s ease-out",
            zIndex: 1, pointerEvents: "none",
            opacity: sliderWidth > 0 ? 1 : 0,
          }}
        />
        <HeaderTab refEl={tab1Ref} active={false} onClick={onBackToOpening}>
          Beállítás
        </HeaderTab>
        <HeaderTab refEl={tab2Ref} active={true}>
          Webinár
        </HeaderTab>
      </div>
    </header>
  );
}

function HeaderTab({ refEl, active, children, onClick }) {
  return (
    <button
      ref={refEl}
      type="button"
      onClick={onClick}
      style={{
        padding: "5px 14px", fontSize: 12, fontWeight: active ? 600 : 500,
        cursor: "pointer",
        borderRadius: 6,
        display: "inline-flex", alignItems: "center", gap: 6,
        transition: "color 0.18s ease-out",
        zIndex: 2, position: "relative",
        background: "transparent", border: "1px solid transparent",
        color: active ? "white" : "#7a7a95",
        whiteSpace: "nowrap", fontFamily: "inherit",
      }}
    >
      {active && (
        <span
          aria-hidden="true"
          style={{
            width: 5, height: 5, borderRadius: "50%",
            background: PURPLE_ACCENT,
            boxShadow: "0 0 6px rgba(124,108,242,0.7)",
          }}
        />
      )}
      {children}
    </button>
  );
}

/* ─────────────────────────── OUTPUT VIEW ─────────────────────────── */

function LandingTitlesOutputView({
  options,
  refineText, setRefineText,
  refining, expandingId, inputDisabled,
  onSubmitChat, onOptionExpand, isMac,
}) {
  const leftTitle = "Elkészültek a címvázlatok";
  const leftBody =
    "Olvasd át a 3 javaslatot jobbra. Kattints arra, amelyikre felépítsük a regisztrációs oldalt, vagy írj lent a chat ablakban, ha új változatokat szeretnél.";
  const placeholder = "Írd ide, milyen irányba finomítsuk a címeket.";

  return (
    <div className="flex flex-col h-full" style={{ background: "#0a0a12" }}>
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">

        {/* LEFT — Finomító Panel */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="landing-titles-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  letterSpacing: "-0.015em",
                  color: "#ffffff",
                  marginBottom: 18,
                }}
              >
                {leftTitle}
              </div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 400,
                  lineHeight: 1.7,
                  color: "#c0c0d0",
                }}
              >
                {leftBody}
              </div>
            </div>
          </div>

          {/* Bottom: chat input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (refineText.trim() && !inputDisabled) onSubmitChat();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="landing-titles-refine-form"
          >
            <div
              style={{
                background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                border: "1px solid rgba(243,238,228,0.14)",
                borderRadius: 12,
                padding: "5px 5px 5px 14px",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                opacity: inputDisabled ? 0.5 : 1,
                pointerEvents: inputDisabled ? "none" : "auto",
              }}
            >
              <button
                type="button"
                aria-label="Csatolás"
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "#8a8a9c", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Paperclip size={14} weight="regular" />
              </button>
              <input
                value={refineText}
                onChange={(e) => setRefineText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (refineText.trim() && !inputDisabled) onSubmitChat();
                  }
                }}
                placeholder={placeholder}
                data-testid="landing-titles-refine-input"
                disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{
                  background: "transparent", border: "none", color: "white",
                  fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                }}
              />
              <button
                type="submit"
                disabled={!refineText.trim() || inputDisabled}
                data-testid="landing-titles-refine-send"
                aria-label="Küldés"
                style={{
                  width: 36, height: 36,
                  background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                  color: "white", borderRadius: 9, border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: refineText.trim() && !inputDisabled ? "pointer" : "not-allowed",
                  opacity: refineText.trim() && !inputDisabled ? 1 : 0.5,
                  boxShadow: refineText.trim() && !inputDisabled
                    ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                    : "none",
                }}
              >
                {refining ? (
                  <CircleNotch size={15} weight="bold" className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={15} weight="fill" />
                )}
              </button>
            </div>
            <div
              className="flex justify-between"
              style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}
            >
              <span>
                {"Pl.: „legyen konkrétabb\", „legyen kíváncsiságkeltőbb\" vagy „szóljon erősebben a kezdőknek\""}
              </span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </div>

        {/* RIGHT — cream blueprint card */}
        <div
          className="overflow-y-auto h-full"
          style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
        >
          <div
            data-preview-theme="cream"
            data-testid="landing-titles-cream-card"
            className="mr-auto"
            style={{
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px",
              maxWidth: 860,
              marginLeft: 0,
              position: "relative",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key="sketches"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
              >
                <h2
                  className="font-heading"
                  style={{
                    fontSize: 26, fontWeight: 700, color: "#0a0a12",
                    letterSpacing: "-0.018em", marginBottom: 10, lineHeight: 1.2,
                  }}
                >
                  Webinárcím-vázlatok
                </h2>
                <p style={{ fontSize: 14, color: "#6a6a78", lineHeight: 1.6, marginBottom: 22, fontWeight: 400 }}>
                  Három különböző irányból írtunk cím- és alcím-párost ehhez a webinárhoz. Válaszd ki, melyikre építsük fel a regisztrációs oldalt. Kattints a kártyára.
                </p>
                <div data-testid="landing-titles-sketches">
                  {options.map((opt, idx) => (
                    <CreamTitleSection
                      key={opt.id || idx}
                      option={opt}
                      index={idx}
                      isLast={idx === options.length - 1}
                      expanding={expandingId === opt.id}
                      onClick={() => onOptionExpand(opt)}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── CreamTitleSection ─────────────────────────── */

function CreamTitleSection({ option, index, isLast, expanding, onClick }) {
  const [hover, setHover] = useState(false);
  // Landing arany akcentus (a Landing agent GOLD_ACCENT.label tokenje) —
  // konverziós arany, ami a Landing típuskártyák és eyebrow-k színvilágát
  // követi. NEM a Facebook poszt kék akcentusa.
  const ACCENT = "#d4a800";
  return (
    <div
      onClick={expanding ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`landing-title-option-${option.id || index}`}
      style={{
        cursor: expanding ? "wait" : "pointer",
        padding: "18px 0",
        borderBottom: isLast ? "none" : "1px solid rgba(10,10,18,0.08)",
        background: hover ? "rgba(212,168,0,0.07)" : "transparent",
        margin: hover ? "0 -16px" : "0",
        paddingLeft: hover ? 16 : 0,
        paddingRight: hover ? 16 : 0,
        borderRadius: hover ? 8 : 0,
        transition: "background 180ms ease",
      }}
    >
      {/* Felső metasor: bal oldalon aranysárga pill webinárnévvel,
          jobb oldalon jobbra zárt IRÁNY metaadat. Kisebb képernyőn
          egymás alá törnek (webinárnév felül, irány alatta). */}
      <div
        className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2"
        style={{ marginBottom: 14 }}
      >
        {option.webinar_name ? (
          <span
            className="font-heading"
            data-testid={`landing-title-webinarname-${index}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              maxWidth: "70%",
              padding: "6px 14px",
              borderRadius: 999,
              background: ACCENT,           // Landing arany (#d4a800)
              color: "#ffffff",
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.35,
              letterSpacing: "-0.005em",
              whiteSpace: "normal",
              wordBreak: "break-word",
              boxShadow: "0 2px 6px rgba(212, 168, 0, 0.20)",
            }}
          >
            {option.webinar_name}
          </span>
        ) : (
          <span />
        )}
        <span
          data-testid={`landing-title-eyebrow-${index}`}
          style={{
            fontSize: 10.5,
            color: ACCENT,
            letterSpacing: "0.20em",
            fontWeight: 600,
            textTransform: "uppercase",
            textAlign: "right",
            opacity: 0.85,
            marginLeft: "auto",
          }}
        >
          IRÁNY: <span style={{ fontWeight: 600 }}>{option.direction || option.id}</span>
        </span>
      </div>
      <h3
        className="font-heading"
        style={{
          fontSize: 22, fontWeight: 700, color: "#0a0a12",
          lineHeight: 1.28, marginBottom: 10, letterSpacing: "-0.015em",
        }}
        data-testid={`landing-title-headline-${index}`}
      >
        {option.title}
      </h3>
      {option.subtitle && (
        <p
          style={{
            fontSize: 14, color: "#4a4a55", lineHeight: 1.55, marginBottom: 10,
            fontWeight: 400,
          }}
          data-testid={`landing-title-subtitle-${index}`}
        >
          {option.subtitle}
        </p>
      )}
      <div
        style={{
          fontSize: 12.5, color: ACCENT, fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}
        data-testid={`landing-title-option-cta-${option.id || index}`}
      >
        {expanding ? (
          <>
            <CircleNotch size={12} weight="bold" className="animate-spin" /> Kibontás…
          </>
        ) : (
          <>Ezzel készítsd el az oldalt →</>
        )}
      </div>
    </div>
  );
}
